import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { formatEUR } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Send, Plus, Trash2, Eye } from "lucide-react";

export default function OfferEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/offers/${id}`);
      setOffer(r.data);
    })();
  }, [id]);

  if (!offer) return <div className="text-slate-500">Wird geladen …</div>;

  const set = (k, v) => setOffer({ ...offer, [k]: v });
  const setItem = (i, k, v) => {
    const items = [...offer.items];
    items[i] = { ...items[i], [k]: v };
    if (k === "quantity" || k === "unit_price") {
      items[i].total = (parseFloat(items[i].quantity) || 0) * (parseFloat(items[i].unit_price) || 0);
    }
    setOffer({ ...offer, items });
  };
  const addItem = () => setOffer({ ...offer, items: [...offer.items, { title: "Neue Position", description: "", quantity: 1, unit_price: 0, total: 0 }] });
  const delItem = (i) => setOffer({ ...offer, items: offer.items.filter((_, idx) => idx !== i) });

  const total = offer.items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/offers/${id}`, { title: offer.title, summary: offer.summary, items: offer.items, benefits: offer.benefits, next_step: offer.next_step });
      toast.success("Gespeichert");
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  const approve = async () => { try { await api.post(`/offers/${id}/approve`); toast.success("Freigegeben"); const r = await api.get(`/offers/${id}`); setOffer(r.data); } catch { toast.error("Fehler"); } };
  const send = async () => { try { await api.post(`/offers/${id}/send`, { origin_url: window.location.origin }); toast.success("Versendet"); const r = await api.get(`/offers/${id}`); setOffer(r.data); } catch { toast.error("Fehler"); } };

  return (
    <div className="space-y-6" data-testid="offer-editor-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Angebot bearbeiten</h1>
          <p className="mt-1 text-slate-600">Anpassen, freigeben und versenden</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={offer.status} />
          <Button data-testid="offer-preview-btn" variant="outline" onClick={() => window.open(`/public/offer/${offer.public_id}`, "_blank")}>
            <Eye className="w-4 h-4 mr-1" /> Vorschau
          </Button>
          <Button data-testid="offer-save-btn" onClick={save} disabled={saving} variant="outline">Speichern</Button>
          {offer.status === "Entwurf" && (
            <Button data-testid="offer-approve-btn" onClick={approve} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle className="w-4 h-4 mr-1" /> Freigeben
            </Button>
          )}
          {(offer.status === "Freigegeben" || offer.status === "Entwurf") && (
            <Button data-testid="offer-send-btn" onClick={send} className="bg-orange-600 hover:bg-orange-700 text-white">
              <Send className="w-4 h-4 mr-1" /> Versenden
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 p-6 space-y-4">
        <Field label="Titel"><Input data-testid="offer-title" value={offer.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Zusammenfassung"><Textarea data-testid="offer-summary" rows={3} value={offer.summary} onChange={(e) => set("summary", e.target.value)} /></Field>
      </Card>

      <Card className="border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Positionen</h2>
          <Button data-testid="add-item-btn" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Position</Button>
        </div>
        <div className="space-y-3">
          {offer.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 p-3 border border-slate-200 rounded-lg">
              <div className="col-span-12 md:col-span-5">
                <Input data-testid={`item-title-${i}`} placeholder="Titel" value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} />
                <Textarea className="mt-2" rows={2} placeholder="Beschreibung" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
              </div>
              <div className="col-span-4 md:col-span-2"><Input type="number" placeholder="Menge" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} /></div>
              <div className="col-span-4 md:col-span-2"><Input type="number" placeholder="Einzelpreis" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} /></div>
              <div className="col-span-3 md:col-span-2 text-right tabular-nums font-medium pt-2">{formatEUR(it.total)}</div>
              <div className="col-span-1 flex items-center justify-end">
                <button data-testid={`del-item-${i}`} onClick={() => delItem(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
          <span className="text-sm text-slate-600">Gesamtpreis</span>
          <span className="text-2xl font-bold tabular-nums">{formatEUR(total)}</span>
        </div>
      </Card>

      <Card className="border-slate-200 p-6 space-y-4">
        <Field label="Nutzen / Vorteile (je Zeile ein Punkt)">
          <Textarea data-testid="offer-benefits" rows={4} value={(offer.benefits || []).join("\n")} onChange={(e) => set("benefits", e.target.value.split("\n").filter(Boolean))} />
        </Field>
        <Field label="Nächster Schritt"><Textarea data-testid="offer-next-step" rows={2} value={offer.next_step} onChange={(e) => set("next_step", e.target.value)} /></Field>
      </Card>

      <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>Hinweis:</strong> Dieser Entwurf muss vor Versand fachlich geprüft und freigegeben werden. Preise, Förder-, Wirtschaftlichkeits-, Garantie- und Technikangaben sind prüfpflichtig.
      </Card>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);
